import axios from 'axios';
import { Logger } from '@elyx-code/common-ts-utils';
import {
	addMoveRecords,
	deleteBatchMoves,
	getPendingMoves,
	IMoveRecord,
	removeMoveRecord,
	updateMoveRecord
} from '../local-relational-database/moves_idb';

export interface IMoveItemInput {
	key: string;
	isFolder?: boolean;
}

export interface IMoveBatchOptions {
	bucket: string;
	items: IMoveItemInput[];
	targetFolderPath: string;
	getAccessToken: () => Promise<string>;
	envConfig: any;
	onProgress?: (completedCount: number, totalCount: number) => void;
	onComplete?: () => void;
	onError?: (error: any) => void;
}

export interface IActiveMoveState {
	id: string; // unique key e.g. `${bucket}:${sourceKey}`
	batchId: string;
	bucket: string;
	sourceKey: string;
	destinationKey: string;
	fileName: string;
	targetFolderPath: string;
	status: 'moving' | 'completed' | 'failed';
	recordId?: number;
}

export interface IBatchProgress {
	batchId: string;
	bucket: string;
	total: number;
	completed: number;
}

export class MoveManagerService {
	private static activeMovesMap = new Map<string, IActiveMoveState>();
	private static abortControllers = new Map<string, AbortController>();
	private static activeRecordIds = new Map<string, number>();
	private static batchProgressMap = new Map<string, IBatchProgress>();
	private static listeners = new Set<() => void>();

	static subscribe(listener: () => void): () => void {
		MoveManagerService.listeners.add(listener);
		return () => {
			MoveManagerService.listeners.delete(listener);
		};
	}

	private static notifyListeners() {
		for (const listener of MoveManagerService.listeners) {
			try {
				listener();
			} catch (e) {
				Logger.error('Error in MoveManagerService listener', e);
			}
		}
	}

	static getActiveMovesForBucket(bucket: string): IActiveMoveState[] {
		const list: IActiveMoveState[] = [];
		for (const move of MoveManagerService.activeMovesMap.values()) {
			if (move.bucket === bucket && move.status === 'moving') {
				list.push(move);
			}
		}
		return list;
	}

	static getBatchProgressForBucket(bucket: string): IBatchProgress | undefined {
		for (const progress of MoveManagerService.batchProgressMap.values()) {
			if (progress.bucket === bucket) {
				return progress;
			}
		}
		return undefined;
	}

	static async cancelBatch(batchId: string) {
		for (const [key, state] of MoveManagerService.activeMovesMap.entries()) {
			if (state.batchId === batchId) {
				const controller = MoveManagerService.abortControllers.get(key);
				if (controller) {
					controller.abort();
					MoveManagerService.abortControllers.delete(key);
				}
				MoveManagerService.activeMovesMap.delete(key);
				MoveManagerService.activeRecordIds.delete(key);
			}
		}
		MoveManagerService.batchProgressMap.delete(batchId);
		await deleteBatchMoves(batchId);
		MoveManagerService.notifyListeners();
	}

	static async moveItemsBatch(options: IMoveBatchOptions): Promise<void> {
		const {
			bucket,
			items,
			targetFolderPath,
			getAccessToken,
			envConfig,
			onProgress,
			onComplete,
			onError
		} = options;

		if (items.length === 0) return;

		const batchId =
			typeof crypto !== 'undefined' && crypto.randomUUID
				? crypto.randomUUID()
				: `batch_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

		// Build records to persist to IndexedDB
		const recordsToInsert: Omit<IMoveRecord, 'id'>[] = items.map((item) => {
			const isFolder = item.isFolder || item.key.endsWith('/');
			const cleanKey =
				isFolder && item.key.endsWith('/') ? item.key.slice(0, -1) : item.key;
			const itemBasename = cleanKey.split('/').pop() || '';
			const destinationKey =
				targetFolderPath + itemBasename + (isFolder ? '/' : '');

			return {
				batchId,
				bucket,
				sourceKey: item.key,
				destinationKey,
				fileName: itemBasename,
				status: 'pending',
				createdAt: Date.now()
			};
		});

		let insertedRecords: IMoveRecord[] = [];
		try {
			insertedRecords = await addMoveRecords(recordsToInsert);
		} catch (err) {
			Logger.error('Failed to store move batch in IndexedDB', err);
		}

		await MoveManagerService.processMoveRecords({
			records:
				insertedRecords.length > 0
					? insertedRecords
					: recordsToInsert.map((r, i) => ({ ...r, id: i + 1 })),
			batchId,
			bucket,
			targetFolderPath,
			getAccessToken,
			envConfig,
			onProgress,
			onComplete,
			onError
		});
	}

	static async resumePendingMovesForBucket(
		bucket: string | undefined,
		getAccessToken: () => Promise<string>,
		envConfig: any
	) {
		try {
			const pendingRecords = await getPendingMoves(bucket);
			if (pendingRecords.length === 0) return;

			// Group by batchId (or bucket)
			const batchGroups = new Map<string, IMoveRecord[]>();
			for (const r of pendingRecords) {
				const bId = r.batchId || `resumed_${r.bucket}`;
				if (!batchGroups.has(bId)) {
					batchGroups.set(bId, []);
				}
				batchGroups.get(bId)!.push(r);
			}

			for (const [bId, records] of batchGroups.entries()) {
				const sample = records[0];
				const targetFolderPath = sample.destinationKey.substring(
					0,
					sample.destinationKey.lastIndexOf('/') + 1
				);

				MoveManagerService.processMoveRecords({
					records,
					batchId: bId,
					bucket: sample.bucket,
					targetFolderPath,
					getAccessToken,
					envConfig
				}).catch((err) => {
					Logger.error('Error executing resumed move batch', err);
				});
			}
		} catch (err) {
			Logger.error('Failed to resume pending moves from IndexedDB', err);
		}
	}

	private static async processMoveRecords(opts: {
		records: IMoveRecord[];
		batchId: string;
		bucket: string;
		targetFolderPath: string;
		getAccessToken: () => Promise<string>;
		envConfig: any;
		onProgress?: (completedCount: number, totalCount: number) => void;
		onComplete?: () => void;
		onError?: (error: any) => void;
	}) {
		const {
			records,
			batchId,
			bucket,
			targetFolderPath,
			getAccessToken,
			envConfig,
			onProgress,
			onComplete,
			onError
		} = opts;

		const totalCount = records.length;
		let completedCount = 0;

		MoveManagerService.batchProgressMap.set(batchId, {
			batchId,
			bucket,
			total: totalCount,
			completed: 0
		});
		MoveManagerService.notifyListeners();

		// Concurrency limit: max 5 parallel move requests
		const CONCURRENCY = 5;
		let index = 0;

		const processNext = async (): Promise<void> => {
			if (index >= records.length) return;

			const record = records[index++];
			const moveKey = `${record.bucket}:${record.sourceKey}`;
			const controller = new AbortController();

			MoveManagerService.abortControllers.set(moveKey, controller);
			if (record.id) {
				MoveManagerService.activeRecordIds.set(moveKey, record.id);
			}

			const activeState: IActiveMoveState = {
				id: moveKey,
				batchId,
				bucket: record.bucket,
				sourceKey: record.sourceKey,
				destinationKey: record.destinationKey,
				fileName: record.fileName,
				targetFolderPath,
				status: 'moving',
				recordId: record.id
			};

			MoveManagerService.activeMovesMap.set(moveKey, activeState);
			MoveManagerService.notifyListeners();

			if (record.id) {
				await updateMoveRecord(record.id, { status: 'moving' }).catch(() => {});
			}

			try {
				const token = await getAccessToken();
				await axios.post(
					`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/move`,
					{
						bucket: record.bucket,
						destinationBucket: record.bucket,
						sourceKey: record.sourceKey,
						destinationKey: record.destinationKey
					},
					{
						headers: { Authorization: `Bearer ${token}` },
						signal: controller.signal
					}
				);

				if (record.id) {
					await removeMoveRecord(record.id).catch(() => {});
				}
				completedCount++;
				const progress = MoveManagerService.batchProgressMap.get(batchId);
				if (progress) {
					progress.completed = completedCount;
				}
				MoveManagerService.activeMovesMap.delete(moveKey);
				MoveManagerService.abortControllers.delete(moveKey);
				MoveManagerService.activeRecordIds.delete(moveKey);
				MoveManagerService.notifyListeners();

				if (onProgress) {
					onProgress(completedCount, totalCount);
				}
			} catch (err: any) {
				if (axios.isCancel(err)) {
					Logger.log(`Move for ${record.sourceKey} was cancelled.`);
				} else {
					Logger.error(`Error moving ${record.sourceKey}:`, err);
					if (record.id) {
						await updateMoveRecord(record.id, { status: 'failed' }).catch(
							() => {}
						);
					}
				}
				completedCount++;
				const progress = MoveManagerService.batchProgressMap.get(batchId);
				if (progress) {
					progress.completed = completedCount;
				}
				MoveManagerService.activeMovesMap.delete(moveKey);
				MoveManagerService.abortControllers.delete(moveKey);
				MoveManagerService.activeRecordIds.delete(moveKey);
				MoveManagerService.notifyListeners();
			}

			return processNext();
		};

		const workers: Promise<void>[] = [];
		for (let i = 0; i < Math.min(CONCURRENCY, records.length); i++) {
			workers.push(processNext());
		}

		try {
			await Promise.all(workers);
			await deleteBatchMoves(batchId).catch(() => {});

			// Preserve empty source folders so S3 flat key store doesn't delete the empty folders
			const sourceFoldersToEnsure = new Set<string>();
			for (const r of records) {
				const cleanSource = r.sourceKey.endsWith('/')
					? r.sourceKey.slice(0, -1)
					: r.sourceKey;
				const lastSlash = cleanSource.lastIndexOf('/');
				if (lastSlash !== -1) {
					const parentFolder = cleanSource.substring(0, lastSlash + 1);
					if (parentFolder) {
						sourceFoldersToEnsure.add(parentFolder);
					}
				}
			}

			if (sourceFoldersToEnsure.size > 0) {
				try {
					const token = await getAccessToken();
					for (const folderPath of sourceFoldersToEnsure) {
						await axios
							.post(
								`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/folders`,
								{
									bucket,
									folderPath
								},
								{
									headers: { Authorization: `Bearer ${token}` }
								}
							)
							.catch((err) => {
								Logger.warn(
									`Failed to preserve source folder ${folderPath}:`,
									err
								);
							});
					}
				} catch (err) {
					Logger.warn('Error preserving source folders after move:', err);
				}
			}

			MoveManagerService.batchProgressMap.delete(batchId);
			MoveManagerService.notifyListeners();

			if (onComplete) {
				onComplete();
			}
		} catch (err) {
			MoveManagerService.batchProgressMap.delete(batchId);
			MoveManagerService.notifyListeners();

			if (onError) {
				onError(err);
			}
		}
	}
}
